import { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import CrudModal from '../../components/admin/CrudModal';
import { adminDelete, adminGet, adminPost, adminPut } from '../../lib/adminApi';

const emptyEvent = {
  title: '',
  client: '',
  technician_id: '',
  technician_name: '',
  start_at: '',
  end_at: '',
  location: '',
  status: 'planifie',
  notes: '',
};

const AdminPlanning = () => {
  const [events, setEvents] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formState, setFormState] = useState(emptyEvent);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [planningData, techniciansData] = await Promise.all([
        adminGet('/admin/planning'),
        adminGet('/admin/technicians'),
      ]);
      setEvents(planningData);
      setTechnicians(techniciansData);
    } catch (error) {
      console.error('Error fetching planning:', error);
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingEvent(null);
    setFormState(emptyEvent);
    setModalOpen(true);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormState({ ...emptyEvent, ...event });
    setModalOpen(true);
  };

  const handleChange = (field, value) => {
    if (field === 'technician_id') {
      const selected = technicians.find((item) => item.id === value);
      setFormState((current) => ({
        ...current,
        technician_id: value,
        technician_name: selected ? selected.name : '',
      }));
      return;
    }
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (editingEvent) {
        await adminPut(`/admin/planning/${editingEvent.id}`, formState);
        toast.success('Évènement mis à jour');
      } else {
        await adminPost('/admin/planning', formState);
        toast.success('Évènement créé');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving planning event:', error);
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Supprimer cet évènement ?')) return;
    try {
      await adminDelete(`/admin/planning/${eventId}`);
      toast.success('Évènement supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting planning event:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onAction={openCreateModal}>
      <Toaster position="top-right" richColors />
      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Planning atelier & terrain</h2>
            <p className="crm-section-subtitle">
              Planifiez les visites et affectez les techniciens.
            </p>
          </div>
        </div>

        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Créneau</th>
                <th>Client</th>
                <th>Intervention</th>
                <th>Technicien</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan="6"><div className="crm-empty">Aucun évènement planifié.</div></td>
                </tr>
              ) : (
                events.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {new Date(item.start_at).toLocaleString('fr-FR')}<br />
                      <span className="crm-mini-card__meta">au {new Date(item.end_at).toLocaleString('fr-FR')}</span>
                    </td>
                    <td>{item.client}</td>
                    <td>{item.title}</td>
                    <td>{item.technician_name || 'Non affecté'}</td>
                    <td>{item.status}</td>
                    <td>
                      <div className="crm-table__actions">
                        <button type="button" className="crm-action-link" onClick={() => openEditModal(item)}>Modifier</button>
                        <button type="button" className="crm-action-danger" onClick={() => handleDelete(item.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {modalOpen ? (
        <CrudModal
          title={editingEvent ? 'Modifier un évènement' : 'Créer un évènement'}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={editingEvent ? 'Mettre à jour' : 'Créer'}
        >
          <div className="crm-form__grid">
            <div className="crm-form__field">
              <label>Titre</label>
              <input value={formState.title} onChange={(e) => handleChange('title', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Client</label>
              <input value={formState.client} onChange={(e) => handleChange('client', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Début</label>
              <input type="datetime-local" value={formState.start_at} onChange={(e) => handleChange('start_at', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Fin</label>
              <input type="datetime-local" value={formState.end_at} onChange={(e) => handleChange('end_at', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Technicien</label>
              <select value={formState.technician_id} onChange={(e) => handleChange('technician_id', e.target.value)}>
                <option value="">Non affecté</option>
                {technicians.map((technician) => (
                  <option key={technician.id} value={technician.id}>{technician.name}</option>
                ))}
              </select>
            </div>
            <div className="crm-form__field">
              <label>Statut</label>
              <select value={formState.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value="planifie">Planifié</option>
                <option value="confirme">Confirmé</option>
                <option value="termine">Terminé</option>
                <option value="annule">Annulé</option>
              </select>
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Lieu</label>
              <input value={formState.location} onChange={(e) => handleChange('location', e.target.value)} />
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Notes</label>
              <textarea value={formState.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
          </div>
        </CrudModal>
      ) : null}
    </AdminLayout>
  );
};

export default AdminPlanning;
